import { useState, useCallback, useRef } from "react";
import { AUTOCOMPLETE_COMMON_COPY } from "@/lib/addressAutocompleteCopy";
import {
  capitalizeWords,
  extractRoadFromDisplayName,
  getSearchVariants,
  includesNormalized,
  matchesStreetQuery,
  normalizeForMatch,
  normalizeRegion,
  startsWithNormalized,
  stripRoadPrefix,
} from "@/lib/geoText";

interface AddressSuggestion {
  display: string;       // Wyświetlana nazwa
  city: string;          // Miasto
  region: string;        // Województwo
  address?: string;      // Ulica (jeśli dostępna)
  postalCode?: string;   // Kod pocztowy
  lat: number;
  lng: number;
  // Internal fields for precise geocoding of house numbers
  _needsPreciseGeocode?: boolean;
  _streetName?: string;
  _houseNumber?: string;
}

interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
  addresstype?: string;
  type?: string;
  class?: string;
  name?: string;
  address?: {
    road?: string;
    house_number?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    county?: string;
    state?: string;
    postcode?: string;
  };
}

const isHighwayLike = (item: NominatimResult): boolean => {
  const cls = (item.class || "").toLowerCase();
  const addresstype = (item.addresstype || "").toLowerCase();
  // Najbardziej sensowne dla autouzupełniania ulic
  return cls === "highway" || addresstype === "road";
};

// Debounce: krótszy dla miast (Photon szybki, krótkie zapytania), dłuższy dla
// adresów (Nominatim ma rate-limit ~1 req/s i często wymaga 2 zapytań).
const DEBOUNCE_MS_CITY = 250;
const DEBOUNCE_MS_ADDRESS = 400;
// Minimalna przerwa między KOLEJNYMI faktycznymi zapytaniami sieciowymi,
// żeby przy szybkim pisaniu nie zalewać Nominatim/Photon.
const MIN_GAP_MS = 350;
// TTL cache odpowiedzi (ten sam klucz pomijany w sieci).
const CACHE_TTL_MS = 60_000;
const CACHE_MAX = 50;

type CacheEntry = { at: number; value: AddressSuggestion[] };
const responseCache = new Map<string, CacheEntry>();

const cacheKey = (type: "city" | "address", query: string, city?: string) =>
  `${type}|${(city ?? "").trim().toLowerCase()}|${query.trim().toLowerCase()}`;

const cacheGet = (key: string): AddressSuggestion[] | null => {
  const hit = responseCache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > CACHE_TTL_MS) {
    responseCache.delete(key);
    return null;
  }
  // Odśwież LRU position
  responseCache.delete(key);
  responseCache.set(key, hit);
  return hit.value;
};

const cacheSet = (key: string, value: AddressSuggestion[]) => {
  responseCache.set(key, { at: Date.now(), value });
  if (responseCache.size > CACHE_MAX) {
    const oldest = responseCache.keys().next().value;
    if (oldest) responseCache.delete(oldest);
  }
};

/** Reset cache — używane głównie w testach. */
export const __resetAddressSuggestionsCache = () => {
  responseCache.clear();
};

export const useAddressSuggestions = () => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchAtRef = useRef<number>(0);

  const fetchSuggestions = useCallback(async (query: string, type: "city" | "address", city?: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const minLength = type === "address" ? 3 : 2;
    if (!query || query.length < minLength) {
      setSuggestions([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Cache hit — pomijamy debounce i sieć całkowicie.
    const key = cacheKey(type, query, city);
    const cached = cacheGet(key);
    if (cached) {
      setSuggestions(cached);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Pokaż "Ładowanie..." natychmiast (przed debounce), aby UI nie wyglądał martwy
    setIsLoading(true);
    setError(null);

    // Throttle: jeśli ostatni request poszedł niedawno, wydłuż debounce, żeby
    // utrzymać minimalny odstęp MIN_GAP_MS między faktycznymi zapytaniami.
    const baseDelay = type === "city" ? DEBOUNCE_MS_CITY : DEBOUNCE_MS_ADDRESS;
    const sinceLast = Date.now() - lastFetchAtRef.current;
    const delay = Math.max(baseDelay, MIN_GAP_MS - sinceLast);

    debounceTimerRef.current = setTimeout(async () => {
      abortControllerRef.current = new AbortController();
      lastFetchAtRef.current = Date.now();

      try {
        // Dla adresów - wyodrębnij numer domu z zapytania (np. "ledochowskiej 5" -> street="ledochowskiej", houseNumber="5")
        let streetQuery = query;
        let userHouseNumber: string | null = null;
        
        if (type === "address") {
          // Szukamy wzorca: tekst + spacja + numer (opcjonalnie z literą, np. "5", "5a", "12/3")
          const houseNumberMatch = query.match(/^(.+?)\s+(\d+[a-zA-Z]?(?:\/\d+[a-zA-Z]?)?)$/);
          if (houseNumberMatch) {
            streetQuery = houseNumberMatch[1].trim();
            userHouseNumber = houseNumberMatch[2];
          }
        }

        const fetchNominatim = async (searchQueryStr: string): Promise<NominatimResult[]> => {
          const params = new URLSearchParams({
            q: searchQueryStr,
            format: "json",
            // Ważne: Nominatim często nie zwraca oczywistych trafień przy krótkich prefiksach,
            // więc pobieramy więcej i filtrujemy/sortujemy po stronie klienta.
            limit: "50",
            countrycodes: "pl",
            addressdetails: "1",
          });

          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?` + params,
            {
              headers: {
                "User-Agent": "GdzieSchronisko-ShelterApp/1.0",
                "Accept-Language": "pl",
              },
              signal: abortControllerRef.current?.signal,
            }
          );

          if (!response.ok) {
            throw new Error("Błąd połączenia");
          }

          return (await response.json()) as NominatimResult[];
        };

        // Dla miast używamy Photon (Komoot) - obsługuje prefiksy ("warsz" → Warszawa,
        // "zielona g" → Zielona Góra). Nominatim wymaga pełnej nazwy.
        if (type === "city") {
          const photonParams = new URLSearchParams({
            q: query,
            limit: "40",
            lang: "default",
            // Bounding box Polski - bez tego Photon zwraca globalne wyniki posortowane po popularności
            // (np. dla "lo" zwraca London/Los Angeles zamiast Łodzi).
            bbox: "14,49,24,55",
          });
          const photonUrl =
            `https://photon.komoot.io/api/?${photonParams}` +
            `&osm_tag=place:city&osm_tag=place:town&osm_tag=place:village`;

          interface PhotonFeature {
            geometry: { coordinates: [number, number] };
            properties: {
              name?: string;
              osm_value?: string;
              countrycode?: string;
              state?: string;
              postcode?: string;
            };
          }

          let photonData: { features: PhotonFeature[] } = { features: [] };
          let photonFailed = false;
          try {
            const photonResp = await fetch(photonUrl, {
              headers: { "Accept-Language": "pl" },
              signal: abortControllerRef.current?.signal,
            });
            if (photonResp.ok) {
              photonData = await photonResp.json();
            } else {
              photonFailed = true;
            }
          } catch (e) {
            if ((e as Error).name === "AbortError") return;
            photonFailed = true;
          }

          const queryTrimmedCity = query.trim();
          const citiesMap = new Map<string, AddressSuggestion>();
          const cityRanks = new Map<string, number>();

          for (const feat of photonData.features) {
            const props = feat.properties || {};
            if ((props.countrycode || "").toUpperCase() !== "PL") continue;
            const cityNameRaw = props.name || "";
            if (!cityNameRaw) continue;
            if (!startsWithNormalized(cityNameRaw, queryTrimmedCity)) continue;

            const [lng, lat] = feat.geometry?.coordinates || [0, 0];
            if (lat < 49 || lat > 55 || lng < 14 || lng > 24) continue;

            const region = normalizeRegion(props.state);
            const cityKey = `${normalizeForMatch(cityNameRaw)}|${normalizeForMatch(region)}`;
            const rank = props.osm_value === "city" ? 0 : props.osm_value === "town" ? 1 : 2;
            if (!cityRanks.has(cityKey) || (cityRanks.get(cityKey) ?? 9) > rank) {
              cityRanks.set(cityKey, rank);
            }
            if (citiesMap.has(cityKey)) continue;

            const cityFormatted = capitalizeWords(cityNameRaw);
            const parts: string[] = [cityFormatted];
            if (region) parts.push(region);
            citiesMap.set(cityKey, {
              display: parts.join(", "),
              city: cityFormatted,
              region,
              postalCode: props.postcode || undefined,
              lat,
              lng,
            });
          }

          const list = Array.from(citiesMap.entries())
            .map(([key, v]) => ({ key, v, rank: cityRanks.get(key) ?? 2 }))
            .sort((a, b) => a.rank - b.rank || a.v.city.localeCompare(b.v.city, "pl"))
            .slice(0, 5)
            .map((x) => x.v);

          if (photonFailed && list.length === 0) {
            setError(AUTOCOMPLETE_COMMON_COPY.fetchError);
          } else if (list.length > 0) {
            cacheSet(key, list);
          }
          setSuggestions(list);
          return;
        }

        // Dla adresów - używamy Nominatim (zdefiniowane wyżej fetchNominatim)

        const queryVariants = getSearchVariants(streetQuery);
        const primaryQuery = queryVariants.length > 1 ? queryVariants[1] : streetQuery;
        const searchQuery = city?.trim()
          ? `${primaryQuery}, ${city}, Polska`
          : `${primaryQuery}, Polska`;

        let data: NominatimResult[] = await fetchNominatim(searchQuery);

        if (data.length === 0 && queryVariants.length > 1) {
          const fallbackSearchQuery = city?.trim()
            ? `${streetQuery}, ${city}, Polska`
            : `${streetQuery}, Polska`;
          data = await fetchNominatim(fallbackSearchQuery);
        }

        const queryTrimmed = streetQuery.trim();

        // Dla adresów - standardowa logika
        const mapAddressResults = (dataIn: NominatimResult[]): AddressSuggestion[] =>
          dataIn
            .filter((item) => {
            const lat = parseFloat(item.lat);
            const lng = parseFloat(item.lon);
            if (lat < 49 || lat > 55 || lng < 14 || lng > 24) return false;
            
            // Dla adresów - sprawdź czy ulica zawiera zapytanie i czy miasto się zgadza
            if (type === "address") {
              // Odfiltruj sklepy/POI - chcemy głównie drogi/ulice
              if (!isHighwayLike(item)) return false;

              const addr = item.address || {};
              const roadRaw = addr.road || "";
              const displayRaw = item.display_name || "";
              const placeCityContextRaw = [
                addr.city,
                addr.town,
                addr.village,
                addr.municipality,
                addr.county,
              ]
                .filter(Boolean)
                .join(" ");
              
              // Jeśli podano miasto, filtruj tylko wyniki z tego miasta
              if (city) {
                // Porównanie odporne na polskie znaki / wielkość liter
                // Uwaga: Nominatim dla ulic w Warszawie potrafi pominąć "city" i podać tylko "county".
                // Jeśli nie mamy żadnego pola miejskiego, nie odrzucamy wyniku (i tak query zawiera miasto).
                if (placeCityContextRaw) {
                  if (
                    !includesNormalized(placeCityContextRaw, city) &&
                    !includesNormalized(city, placeCityContextRaw)
                  ) {
                    return false;
                  }
                }
              }

              // Dopasowanie tokenów (prefiks) odporne na polskie znaki.
              const roadCandidate = stripRoadPrefix(roadRaw) || extractRoadFromDisplayName(displayRaw);
              if (roadCandidate) return matchesStreetQuery(roadCandidate, queryTrimmed);
              return matchesStreetQuery(displayRaw, queryTrimmed);
            }
            
            return true;
            })
            .map((item) => {
            const addr = item.address || {};
            const cityRaw = addr.city || addr.town || addr.village || addr.municipality || "";
            const cityResolvedRaw = cityRaw || city || "";
            const cityResolved = capitalizeWords(cityResolvedRaw);
            const region = normalizeRegion(addr.state);
            
            let addressStr = "";
            // Ulica: preferuj addr.road, ale gdy jest zbyt uboga, spróbuj wyciągnąć z display_name
            const roadRaw = addr.road || "";
            const roadCandidate = stripRoadPrefix(roadRaw) || extractRoadFromDisplayName(item.display_name);

            // Zapamiętaj numer domu (od użytkownika lub z Nominatim)
            let finalHouseNumber: string | null = null;
            if (roadCandidate) {
              addressStr = capitalizeWords(roadCandidate);
              // Jeśli użytkownik podał numer domu w zapytaniu, użyj go; w przeciwnym razie użyj numeru z Nominatim
              if (userHouseNumber) {
                addressStr += ` ${userHouseNumber}`;
                finalHouseNumber = userHouseNumber;
              } else if (addr.house_number) {
                addressStr += ` ${addr.house_number}`;
                finalHouseNumber = addr.house_number;
              }
            }

            // Anty-śmieci: odrzuć "Polska" jako "ulicę"
            if (type === "address" && addressStr.trim().toLowerCase() === "polska") {
              addressStr = "";
            }

            // Wyświetlana nazwa
            const parts: string[] = [];
            if (type === "address") {
              if (addressStr) parts.push(addressStr);
              if (cityResolved) parts.push(cityResolved);
            } else {
              if (cityResolved) parts.push(cityResolved);
            }
            if (region) parts.push(region);
            const display = parts.join(", ");

            return {
              display,
              city: cityResolved,
              region,
              address: addressStr || undefined,
              postalCode: addr.postcode || undefined,
              lat: parseFloat(item.lat),
              lng: parseFloat(item.lon),
              // Przekaż informację o numerze domu i oryginalnej nazwie ulicy do późniejszego geokodowania
              _needsPreciseGeocode: !!finalHouseNumber,
              _streetName: roadCandidate || undefined,
              _houseNumber: finalHouseNumber || undefined,
            };
            });

        let mapped: AddressSuggestion[] = mapAddressResults(data);

        // Fallback: czasem zapytanie z dopisanym miastem zwraca 0 wyników (np. "urszuli, Warszawa, Polska"),
        // ale bez miasta już zwraca trafienia, które możemy potem przefiltrować po Warszawie.
        if (type === "address" && city?.trim() && mapped.length === 0) {
          const fallbackQuery = `${queryTrimmed}, Polska`;
          const fallbackData = await fetchNominatim(fallbackQuery);
          mapped = mapAddressResults(fallbackData);
        }

        // Fallback Photon: Nominatim NIE wspiera wyszukiwania po prefiksie nazwy ulicy
        // (np. "ledochowsk" zwraca 0, dopiero pełne "ledóchowskiej" trafia).
        // Photon (Komoot) obsługuje prefiksy i polskie znaki diakrytyczne. Używamy go,
        // gdy Nominatim nie dał rezultatów dla zapytania ulicowego.
        if (type === "address" && mapped.length === 0) {
          interface PhotonStreetFeature {
            geometry: { coordinates: [number, number] };
            properties: {
              name?: string;
              street?: string;
              city?: string;
              town?: string;
              village?: string;
              county?: string;
              state?: string;
              postcode?: string;
              osm_value?: string;
              osm_key?: string;
              countrycode?: string;
              housenumber?: string;
            };
          }
          const photonStreetUrl =
            `https://photon.komoot.io/api/?q=${encodeURIComponent(streetQuery + (city ? " " + city : ""))}` +
            `&limit=20&lang=default&bbox=14,49,24,55&osm_tag=highway`;
          try {
            const resp = await fetch(photonStreetUrl, {
              headers: { "Accept-Language": "pl" },
              signal: abortControllerRef.current?.signal,
            });
            if (resp.ok) {
              const json = (await resp.json()) as { features: PhotonStreetFeature[] };
              const photonStreets: AddressSuggestion[] = [];
              for (const feat of json.features || []) {
                const p = feat.properties || {};
                if ((p.countrycode || "").toUpperCase() !== "PL") continue;
                // Tylko prawdziwe drogi: residential/primary/secondary/tertiary/living_street/pedestrian itp.
                const allowedHighway = new Set([
                  "residential", "primary", "secondary", "tertiary", "unclassified",
                  "living_street", "pedestrian", "service", "track",
                ]);
                if (p.osm_key !== "highway" || !allowedHighway.has(p.osm_value || "")) continue;
                const roadName = p.name || p.street || "";
                if (!roadName) continue;
                const cityResolvedRaw = p.city || p.town || p.village || city || "";
                if (city && cityResolvedRaw && !includesNormalized(cityResolvedRaw, city)) continue;
                if (!matchesStreetQuery(roadName, queryTrimmed)) continue;
                const [lng, lat] = feat.geometry?.coordinates || [0, 0];
                if (lat < 49 || lat > 55 || lng < 14 || lng > 24) continue;
                const cityResolved = capitalizeWords(cityResolvedRaw);
                const region = normalizeRegion(p.state);
                let addressStr = capitalizeWords(stripRoadPrefix(roadName));
                let finalHouseNumber: string | null = null;
                if (userHouseNumber) {
                  addressStr += ` ${userHouseNumber}`;
                  finalHouseNumber = userHouseNumber;
                }
                const parts: string[] = [];
                if (addressStr) parts.push(addressStr);
                if (cityResolved) parts.push(cityResolved);
                if (region) parts.push(region);
                photonStreets.push({
                  display: parts.join(", "),
                  city: cityResolved,
                  region,
                  address: addressStr || undefined,
                  postalCode: p.postcode || undefined,
                  lat,
                  lng,
                  _needsPreciseGeocode: !!finalHouseNumber,
                  _streetName: stripRoadPrefix(roadName),
                  _houseNumber: finalHouseNumber || undefined,
                });
              }
              mapped = photonStreets;
            }
          } catch (e) {
            if ((e as Error).name === "AbortError") return;
          }
        }


        // Usuń duplikaty po display i ogranicz do 5 wyników
        const unique = mapped
          .filter((s) => Boolean(s.display?.trim()))
          .filter(
            (item, index, self) =>
              index === self.findIndex((t) => t.display === item.display)
          )
          .sort((a, b) => {
            const aAddr = a.address || "";
            const bAddr = b.address || "";

            const aStarts = startsWithNormalized(aAddr, queryTrimmed);
            const bStarts = startsWithNormalized(bAddr, queryTrimmed);
            if (aStarts !== bStarts) return aStarts ? -1 : 1;

            const aIncludes = includesNormalized(aAddr, queryTrimmed);
            const bIncludes = includesNormalized(bAddr, queryTrimmed);
            if (aIncludes !== bIncludes) return aIncludes ? -1 : 1;

            return a.display.localeCompare(b.display, "pl");
          })
          .slice(0, 5);

        if (unique.length > 0) cacheSet(key, unique);
        setSuggestions(unique);
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          console.error("Błąd pobierania sugestii:", err);
          setError(AUTOCOMPLETE_COMMON_COPY.fetchError);
        }
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, delay);
  }, []);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setError(null);
  }, []);

  return {
    suggestions,
    isLoading,
    error,
    fetchSuggestions,
    clearSuggestions,
  };
};
